type RetiredRegistration = {
  unregister(): Promise<boolean>;
};

type RetiredServiceWorkerContainer = {
  controller: unknown;
  getRegistrations(): Promise<readonly RetiredRegistration[]>;
};

type RetiredCacheStorage = {
  delete(cacheName: string): Promise<boolean>;
  keys(): Promise<string[]>;
};

export type ServiceWorkerRetirementResult = {
  cachesDeleted: number;
  controlled: boolean;
  registrationsFound: number;
  registrationsRemoved: number;
};

function browserServiceWorkers(): RetiredServiceWorkerContainer | undefined {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
    return undefined;
  return navigator.serviceWorker;
}

function browserCaches(): RetiredCacheStorage | undefined {
  return typeof caches === "undefined" ? undefined : caches;
}

/**
 * Removes registrations and CacheStorage entries left by Autar's retired PWA.
 * CacheStorage is origin-scoped and the current client does not use it, so
 * clearing every entry avoids coupling recovery to historical Workbox names.
 */
export async function retireLegacyServiceWorkers(
  serviceWorkers = browserServiceWorkers(),
  cacheStorage = browserCaches(),
): Promise<ServiceWorkerRetirementResult> {
  const controlled = Boolean(serviceWorkers?.controller);
  const [registrations, cacheNames] = await Promise.all([
    serviceWorkers?.getRegistrations().catch(() => []) ?? [],
    cacheStorage?.keys().catch(() => []) ?? [],
  ]);
  const [registrationResults, cacheResults] = await Promise.all([
    Promise.allSettled(
      registrations.map((registration) => registration.unregister()),
    ),
    Promise.allSettled(
      cacheNames.map((cacheName) => cacheStorage!.delete(cacheName)),
    ),
  ]);

  return {
    controlled,
    registrationsFound: registrations.length,
    registrationsRemoved: registrationResults.filter(
      (result) => result.status === "fulfilled" && result.value,
    ).length,
    cachesDeleted: cacheResults.filter(
      (result) => result.status === "fulfilled" && result.value,
    ).length,
  };
}
